import { spelledNoteLabel, type PitchQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import type { CapturePhase } from './capture-session.js'
import { PitchInputMeter, type PitchMeterState } from './pitch-input-meter.js'
import { useVocalCopy } from './use-vocal-copy.js'
import styles from './pitch-question.module.css'

/**
 * The per-item panel: what to sing, where in the session we are, and what the capture window is
 * doing right now.
 *
 * The phase line is the singer's only cue, so it matters that `lead-in` does not say "listening".
 * That part of the window belongs to the noise floor (§5.1); a singer who starts inside it
 * raises the floor to the level of their own voice, after which nothing they sing clears the
 * gate and the tool tells them it heard nothing.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.1, §6
 */

/**
 * The singer's cue line, one word per phase, in the teacher's own sequence: listen, [the pitch],
 * breathe, sing. These are UI status strings and not lexicon copy — they say what the machine is
 * doing right now, which is not pedagogy, and no §12 entry covers them. The `reference` phase
 * has no fixed string at all: it shows the material's own name, because that is the thing the
 * singer is being asked to hold.
 */
const PHASE_TEXT: Record<Exclude<CapturePhase, 'reference'>, string> = {
  'count-in': 'Listen…',
  'lead-in': 'Breathe',
  listening: 'Sing',
  scoring: 'Scoring…',
}

export interface PitchQuestionPanelProps {
  question: PitchQuestion
  /** The reference after transposition into the singer's range — what actually sounds. */
  reference: number[]
  index: number
  total: number
  /** Null between items, when nothing is running. */
  phase: CapturePhase | null
  /** Live input, shown while the window is open so a dead mic is visible before the take. */
  meter: PitchMeterState
  onHearIt: () => void
}

export const PitchQuestionPanel: FC<PitchQuestionPanelProps> = props => {
  const { question, reference, index, total, phase, meter, onHearIt } = props
  const exercise = useVocalCopy(question.lexiconId)
  /** Any phase at all means something is sounding or being recorded; a replay would bleed. */
  const busy = phase !== null

  return (
    <section className={styles.panel} aria-label="Sing this back">
      <div className={styles.header}>
        <h2 className={styles.title}>{exercise?.displayName ?? question.type}</h2>
        <span className={styles.position}>
          {index + 1} / {total}
        </span>
      </div>

      {exercise ? (
        <p className={styles.body}>
          <TheoryGlyphs>{exercise.body}</TheoryGlyphs>
        </p>
      ) : null}

      <ol className={styles.notes}>
        {question.interval === null ? (
          reference.map((midi, position) => (
            <li key={`${position}-${midi}`} className={styles.note}>
              {spelledNoteLabel(question.spelling[position], midi)}
            </li>
          ))
        ) : (
          /**
           * Only the root, and the interval by name (GH #2328 item 3). Printing the second note
           * would hand the singer the answer the exercise asks them to find (spec §6).
           */
          <li className={styles.note}>
            {spelledNoteLabel(question.spelling[0], reference[0])} + {question.interval} ↑
          </li>
        )}
      </ol>

      <div className={styles.status} role="status" aria-label="Capture" data-phase={phase ?? 'idle'}>
        {phase === null ? 'Ready when you are' : phase === 'reference' ? question.label : PHASE_TEXT[phase]}
      </div>

      {phase === 'lead-in' || phase === 'listening' ? (
        <PitchInputMeter frame={meter.frame} noiseFloorRms={meter.noiseFloorRms} deviceLabel={meter.deviceLabel} />
      ) : null}

      <Button variant="outlined" size="small" disabled={busy} onClick={onHearIt}>
        Hear it again
      </Button>
    </section>
  )
}
